import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Save, Plus, Trash, X } from 'lucide-react';
import { PhotoEvidenceModule } from '../attachments/PhotoEvidenceModule';
import { printMovement } from '../print/printMovement';
import { api } from '../../services/api';

export function TransferModule({ materials, brandItems = [], warehouses = [], onRegister }) {
  const [originWarehouseId, setOriginWarehouseId] = useState(warehouses[0]?.id || 'popayan');
  const [destinationWarehouseId, setDestinationWarehouseId] = useState(warehouses[1]?.id || 'santander');
  
  const [materialId, setMaterialId] = useState('');
  const [materialQuery, setMaterialQuery] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [observations, setObservations] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [serial, setSerial] = useState('');
  const [showSerialField, setShowSerialField] = useState(false);
  const [serialRowsInOrigin, setSerialRowsInOrigin] = useState([]);
  const [serialsLoading, setSerialsLoading] = useState(false);
  const [items, setItems] = useState([]); 
  const [serialOption, setSerialOption] = useState('No'); 
  const [selectedBrand, setSelectedBrand] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todas');
  const [evidencePhotos, setEvidencePhotos] = useState([]);
  const [, setLastReceipt] = useState(null);
  const [isMaterialDropdownOpen, setIsMaterialDropdownOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsMaterialDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const selectedMaterial = useMemo(() => {
    const id = parseInt(materialId);
    return materials.find((m) => m.id === id) || null;
  }, [materialId, materials]);

  useEffect(() => {
    setMaterialId('');
    setMaterialQuery('');
  }, []);

  useEffect(() => {
    const requiresSerial = !!selectedMaterial?.requiresSerial;
    const opt = requiresSerial ? 'Sí' : 'No';
    setSerialOption(opt);
    setShowSerialField(opt === 'Sí');
    if (!requiresSerial) setSerial('');
    setSelectedBrand('');
    setSerialRowsInOrigin([]);
  }, [selectedMaterial]);

  useEffect(() => {
    if (!showSerialField) return;
    setQuantity(1);
  }, [showSerialField]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!showSerialField || !selectedMaterial?.id || !originWarehouseId) {
        setSerialRowsInOrigin([]);
        return;
      }
      setSerialsLoading(true);
      try {
        const resp = await api.brandItems.get({ material_code: selectedMaterial.code, material_id: selectedMaterial.id, warehouse_id: originWarehouseId, include_deleted: 1, limit: 20000, offset: 0, order: 'asc' });
        const rows = Array.isArray(resp?.data) ? resp.data : (resp?.data ? [resp.data] : []);
        const clean = rows
          .map((r) => ({
            serial: String(r?.serial || '').trim(),
            brand: String(r?.brand || '').trim(),
            warehouse_id: String(r?.warehouse_id || '').trim(),
          }))
          .filter((r) => r.serial && r.warehouse_id === String(originWarehouseId));
        if (!cancelled) setSerialRowsInOrigin(clean);
      } catch (_) {
        if (!cancelled) setSerialRowsInOrigin([]);
      } finally {
        if (!cancelled) setSerialsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showSerialField, selectedMaterial?.id, selectedMaterial?.code, originWarehouseId]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    (materials || []).forEach((m) => {
      const c = String(m.category || m.type || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [materials]);

  const materialOptions = useMemo(() => {
    const list = materials || [];
    let filtered = (categoryFilter && categoryFilter !== 'Todas')
      ? list.filter((m) => {
          const cat = String(m.category || m.type || '').trim();
          return norm(cat).includes(norm(categoryFilter));
        })
      : list;

    // Filter by stock in origin warehouse
    if (originWarehouseId) {
      filtered = filtered.filter(m => {
        const qty = m.stockByWarehouse?.[originWarehouseId];
        return qty && Number(qty) > 0;
      });
    }

    return filtered.map((m) => {
      const stock = m.stockByWarehouse?.[originWarehouseId] || 0;
      return {
        id: m.id,
        label: m.code ? `${m.code} - ${m.name} (Stock: ${stock})` : `${m.id} - ${m.name} (Stock: ${stock})`
      };
    });
  }, [materials, categoryFilter, originWarehouseId]);

  useEffect(() => {
    if (materialId && !materialOptions.find(o => String(o.id) === String(materialId))) {
      setMaterialId('');
      setMaterialQuery('');
    }
  }, [originWarehouseId, materialOptions, materialId]);

  const filteredMaterialOptions = useMemo(() => {
    if (!materialQuery) return materialOptions;
    const q = norm(materialQuery);
    return materialOptions.filter(o => norm(o.label).includes(q));
  }, [materialOptions, materialQuery]);

  const brandsForMaterial = useMemo(() => {
    const selectedType = selectedMaterial?.type || selectedMaterial?.category || '';
    if (!selectedType) return [];
    const byType = (brandItems || []).filter((b) => {
      const biType = (b.materialType || '').trim();
      if (biType) return (biType === selectedType) && (b.status === 'Activo');
      const code = (b.materialCode || '').trim();
      if (!code) return false;
      const mat = (materials || []).find(m => String(m.code) === String(code));
      const mt = mat?.type || mat?.category || '';
      return (mt === selectedType) && (b.status === 'Activo');
    });
    const seen = new Set();
    const unique = [];
    for (const b of byType) {
      const nm = String(b.name || '').trim().toLowerCase();
      if (!nm) continue;
      if (!seen.has(nm)) { seen.add(nm); unique.push(b); }
    }
    return unique;
  }, [brandItems, selectedMaterial, materials]);

  useEffect(() => {
    if (!selectedBrand) return;
    const normBrand = (v) => String(v || '').trim().toLowerCase();
    const list = showSerialField
      ? (serialRowsInOrigin || []).map((r) => String(r?.brand || '').trim()).filter(Boolean)
      : (brandsForMaterial || []).map((b) => b.name);
    const stillValid = list.some((b) => normBrand(b) === normBrand(selectedBrand));
    if (!stillValid) setSelectedBrand('');
  }, [selectedBrand, brandsForMaterial, serialRowsInOrigin, showSerialField]);

  const brandsInOrigin = useMemo(() => {
    const seen = new Set();
    const list = [];
    (serialRowsInOrigin || []).forEach((r) => {
      const nm = String(r.brand || '').trim();
      if (!nm) return;
      const key = nm.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      list.push(nm);
    });
    return list.sort((a, b) => a.localeCompare(b));
  }, [serialRowsInOrigin]);

  const filteredSerialsInOrigin = useMemo(() => {
    const brandSel = String(selectedBrand || '').trim().toLowerCase();
    const list = Array.isArray(serialRowsInOrigin) ? serialRowsInOrigin : [];
    const filtered = brandSel ? list.filter((r) => String(r.brand || '').trim().toLowerCase() === brandSel) : list;
    return filtered.map((r) => r.serial);
  }, [serialRowsInOrigin, selectedBrand]);

  useEffect(() => {
    if (!showSerialField) return;
    if (!serial) return;
    const exists = (serialRowsInOrigin || []).some((r) => r.serial === serial);
    if (!exists) setSerial('');
  }, [showSerialField, serialRowsInOrigin, serial]);

  const canSubmitSingle = useMemo(() => {
    return materialId && Number(quantity) > 0 && originWarehouseId && destinationWarehouseId && originWarehouseId !== destinationWarehouseId;
  }, [materialId, quantity, originWarehouseId, destinationWarehouseId]);

  const canSubmitBulk = useMemo(() => {
    return items.length > 0 || canSubmitSingle;
  }, [items.length, canSubmitSingle]);

  const getStockInOrigin = (matId) => {
    const m = materials.find(x => x.id === parseInt(matId));
    if (!m) return 0;
    return m.stockByWarehouse?.[originWarehouseId] || 0;
  };

  const handleAddItem = () => {
    if (!canSubmitSingle) return;
    
    if (originWarehouseId === destinationWarehouseId) {
        setStatusMsg('La bodega de origen y destino deben ser diferentes.');
        return;
    }

    const currentStock = getStockInOrigin(materialId);
    if (quantity > currentStock) {
        setStatusMsg(`Stock insuficiente en bodega origen. Disponible: ${currentStock}`);
        return;
    }

    if (serialOption === 'Sí' && !serial.trim()) {
      setStatusMsg('Ingrese la serie.');
      return;
    }
    
    const m = materials.find(x => x.id === parseInt(materialId));
    
    const newItem = {
      materialId,
      materialName: m?.name,
      materialCode: m?.code,
      quantity,
      observations,
      serial: (serialOption === 'Sí') ? serial.trim() : '',
      brandName: selectedBrand || '',
      originWarehouseId,
      destinationWarehouseId,
      evidencePhotos,
    };
    setItems((prev) => [...prev, newItem]);
    
    // Reset fields but keep warehouses
    setQuantity(1);
    setSerial('');
    setSelectedBrand('');
    setMaterialId('');
    setMaterialQuery('');
    setShowSerialField(false);
    setSerialOption('No');
    setStatusMsg(null);
  };

  const handleDeleteItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitBulk) return;

    if (items.length === 0 && originWarehouseId === destinationWarehouseId) {
        setStatusMsg('La bodega de origen y destino deben ser diferentes.');
        return;
    }
    
    // Validate stock for single item submission if not added to list
    if (items.length === 0) {
        const currentStock = getStockInOrigin(materialId);
        if (quantity > currentStock) {
            setStatusMsg(`Stock insuficiente en bodega origen. Disponible: ${currentStock}`);
            return;
        }
    }

    const transferRef = 'TRAS-' + new Date().toISOString().replace(/[-:.TZ]/g,'').slice(0,14) + '-' + Math.floor(Math.random()*1000);

    const batch = items.length > 0
      ? items
      : [{ 
          materialId, 
          quantity, 
          observations, 
          serial: showSerialField ? (serial || '') : '', 
          brandName: selectedBrand || '', 
          originWarehouseId, 
          destinationWarehouseId, 
          evidencePhotos 
        }];

    let ok = 0;
    let failed = 0;
    let lastItem = null;
    
    for (const it of batch) {
      const res = await onRegister({ ...it, transferRef });
      if (res?.success) ok++; else failed++;
      lastItem = it;
    }
    
    setStatusMsg(`Traslado: ${ok} ok, ${failed} error(es)`);
    
    if (failed === 0) {
      // Print receipt
      try {
        const itemsForPrint = batch.map((it) => {
          const m = materials.find(mm => String(mm.id) === String(it.materialId));
          return {
            materialName: m?.name || String(it.materialId),
            materialCode: m?.code || '',
            materialUnit: m?.unit || 'Unidad',
            quantity: parseInt(it.quantity),
            serial: String(it.serial || '').trim(),
            brandName: String(it.brandName || '').trim()
          };
        });

        const receipt = {
          type: 'Traslado',
          date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
          origin: warehouses.find(w => w.id === lastItem.originWarehouseId)?.name || lastItem.originWarehouseId,
          destination: warehouses.find(w => w.id === lastItem.destinationWarehouseId)?.name || lastItem.destinationWarehouseId,
          observations: lastItem.observations || '',
          transferRef,
          items: itemsForPrint,
          evidencePhotos: lastItem.evidencePhotos
        };
        setLastReceipt(receipt);
        printMovement(receipt);
      } catch (err) {
        console.error("Error printing receipt", err);
      }

      setItems([]);
      setMaterialId('');
      setMaterialQuery('');
      setQuantity(1);
      setObservations('');
      setSerial('');
      setShowSerialField(false);
      setSelectedBrand('');
      setEvidencePhotos([]);
    }
  };

  return (
    <div className="bg-white p-4 rounded shadow mb-6">
      <h3 className="text-lg font-bold mb-4 border-b pb-2">Traslado entre Bodegas</h3>
      
      {statusMsg && (
        <div className={`p-2 mb-4 rounded ${statusMsg.includes('error') || statusMsg.includes('insuficiente') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {statusMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Bodega Origen</label>
                <select
                    className="mt-1 block w-full border border-gray-300 rounded p-2"
                    value={originWarehouseId}
                    onChange={e => setOriginWarehouseId(e.target.value)}
                >
                    {warehouses.map(w => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Bodega Destino</label>
                <select
                    className="mt-1 block w-full border border-gray-300 rounded p-2"
                    value={destinationWarehouseId}
                    onChange={e => setDestinationWarehouseId(e.target.value)}
                >
                    {warehouses.map(w => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                </select>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Categoría</label>
            <select
              className="mt-1 block w-full border border-gray-300 rounded p-2"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="Todas">Todas</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="relative" ref={wrapperRef}>
            <label className="block text-sm font-medium text-gray-700">Material</label>
            <input
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded p-2"
              placeholder="Buscar material..."
              value={materialQuery}
              onFocus={() => setIsMaterialDropdownOpen(true)}
              onChange={(e) => {
                setMaterialQuery(e.target.value);
                setIsMaterialDropdownOpen(true);
              }}
            />
            {isMaterialDropdownOpen && filteredMaterialOptions.length > 0 && (
              <ul className="absolute z-10 w-full bg-white border border-gray-300 max-h-60 overflow-y-auto shadow-lg">
                {filteredMaterialOptions.map((opt) => (
                  <li
                    key={opt.id}
                    className="p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setMaterialId(String(opt.id));
                      setMaterialQuery(opt.label);
                      setIsMaterialDropdownOpen(false);
                    }}
                  >
                    {opt.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {selectedMaterial && (
            <div className="bg-blue-50 p-2 rounded text-sm text-blue-800">
                Stock en Origen ({warehouses.find(w => w.id === originWarehouseId)?.name}): <strong>{selectedMaterial.stockByWarehouse?.[originWarehouseId] || 0}</strong>
            </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
          <div>
            <label className="block text-sm font-medium text-gray-700">Cantidad</label>
            <input
              type="number"
              min="1"
              className="mt-1 block w-full border border-gray-300 rounded p-2"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={showSerialField}
            />
          </div>

          <div>
             <label className="block text-sm font-medium text-gray-700">¿Tiene Serie?</label>
             <select
               className="mt-1 block w-full border border-gray-300 rounded p-2"
               value={serialOption}
               onChange={(e) => {
                 const next = e.target.value;
                 setSerialOption(next);
                 setShowSerialField(next === 'Sí');
                 if (next === 'No') {
                   setSerial('');
                   setSerialRowsInOrigin([]);
                 } else {
                   setQuantity(1);
                 }
               }}
             >
               <option value="No">No</option>
               <option value="Sí">Sí</option>
             </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Marca</label>
            {showSerialField ? (
              <select
                className="mt-1 block w-full border border-gray-300 rounded p-2"
                value={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  setSerial('');
                }}
                disabled={serialsLoading}
              >
                <option value="">{serialsLoading ? 'Cargando...' : 'Seleccione...'}</option>
                {brandsInOrigin.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            ) : brandsForMaterial.length > 0 ? (
              <select
                className="mt-1 block w-full border border-gray-300 rounded p-2"
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
              >
                <option value="">Seleccione...</option>
                {brandsForMaterial.map((b) => (
                  <option key={b.id} value={b.name}>{b.name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded p-2"
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                placeholder="Marca"
              />
            )}
          </div>

          {showSerialField && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Serie</label>
              {filteredSerialsInOrigin.length > 0 ? (
                <select
                  className="mt-1 block w-full border border-gray-300 rounded p-2"
                  value={serial}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSerial(next);
                    const row = (serialRowsInOrigin || []).find((r) => r.serial === next);
                    const b = String(row?.brand || '').trim();
                    if (b) setSelectedBrand(b);
                  }}
                  disabled={serialsLoading}
                >
                  <option value="">{serialsLoading ? 'Cargando...' : 'Seleccione...'}</option>
                  {filteredSerialsInOrigin.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded p-2"
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder={serialsLoading ? 'Cargando...' : 'Serie'}
                />
              )}
            </div>
          )}
        </div>
        
        <div>
            <label className="block text-sm font-medium text-gray-700">Observaciones</label>
            <textarea
                className="mt-1 block w-full border border-gray-300 rounded p-2 h-24 resize-y"
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
            />
        </div>

        <PhotoEvidenceModule
            photos={evidencePhotos}
            onPhotosChange={setEvidencePhotos}
        />



        {items.length > 0 && (
          <div className="mt-4">
            <h4 className="font-bold mb-2">Ítems a trasladar</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 border">Material</th>
                    <th className="p-2 border">Cant</th>
                    <th className="p-2 border">Origen</th>
                    <th className="p-2 border">Destino</th>
                    <th className="p-2 border">Serie</th>
                    <th className="p-2 border">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="p-2 border">{it.materialCode} - {it.materialName}</td>
                      <td className="p-2 border">{it.quantity}</td>
                      <td className="p-2 border">{warehouses.find(w => w.id === it.originWarehouseId)?.name}</td>
                      <td className="p-2 border">{warehouses.find(w => w.id === it.destinationWarehouseId)?.name}</td>
                      <td className="p-2 border">{it.serial || '-'}</td>
                      <td className="p-2 border text-center">
                        <button type="button" onClick={() => handleDeleteItem(idx)} className="text-red-600 hover:text-red-800">
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-2 border-t pt-4">
            <button
                type="button"
                onClick={handleAddItem}
                disabled={!canSubmitSingle}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
                <Plus size={16} className="mr-1" /> Agregar Ítem
            </button>
          <button
            type="button"
            onClick={() => {
                setItems([]);
                setMaterialId('');
                setQuantity(1);
                setObservations('');
                setSerial('');
                setShowSerialField(false);
                setSelectedBrand('');
                setStatusMsg(null);
            }}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center"
          >
            <X size={16} className="mr-1" /> Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmitBulk}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 flex items-center"
          >
            <Save size={16} className="mr-1" /> Guardar Traslado
          </button>
        </div>
      </form>
    </div>
  );
}
